import { computed } from 'vue';

import Utils from '@/api/Utils';
import MathFunctions from '@/api/MathFunctions';

import { dayJsToString } from '@/components/common/dateUtils';
import {
	getBaseOptions,
	generateRangeSeries, generateSeries,
	pointEntryFactoryXYDay,
} from '@/utils/chartsUtils';


const calcCorrectionValues = (mountpoints, paasCoords, valuesCount) => {
	let result = [];
	for (const mountpoint of mountpoints) {
		let coords = paasCoords.filter(item => item.mountpoint === mountpoint);
		if (coords.length > 0) {
			coords.sort(Utils.objectFieldMultiSorter('rinex_date', 'string', true));
			coords = coords.slice(0, valuesCount);
			const avgVector = MathFunctions.calcAverageVector(coords);
			const stdVector = MathFunctions.calcStdVector(coords, avgVector);
			const stdVectorLength = MathFunctions.calcVectorLength(stdVector.x_coord, stdVector.y_coord, stdVector.z_coord);
			const distance = MathFunctions.calcVectorDiffLength(
				MathFunctions.calcAverage(coords, 'x_coord'),
				MathFunctions.calcAverage(coords, 'y_coord'),
				MathFunctions.calcAverage(coords, 'z_coord'),
				coords[0].x_old,
				coords[0].y_old,
				coords[0].z_old,
			);
			result.push({
				mountpoint,
				distance: Utils.formatMilliMetres(Math.round(distance)),
				std: Utils.formatMilliMetres(Math.round(stdVectorLength)),
				dates: coords.map(item => item.rinex_date),
			});
		}
	}
	return result;
};


const getSeriesKey = item => item.mountpoint;
const getRangeValue = item => [
	item.distance - item.std,
	item.distance + item.std,
];
const getMedianValue = item => item.distance;
const getStddevValue = item => item.std;


const tooltipGenerator = (dataKeys, noDataText) => ({ seriesIndex, dataPointIndex, w }) => {
	const pointData = w.globals.seriesRange[seriesIndex][dataPointIndex].y[0];
	const distance = (pointData.y2 + pointData.y1) / 2;
	const std = (pointData.y2 - pointData.y1) / 2;
	let text = (
		'<div class="apexcharts-tooltip-rangebar"><div> <span class="series-name" style="color:' +
		w.globals.colors[seriesIndex] + '">' +
		w.globals.seriesNames[seriesIndex] + '</span> <span class="category">' +
		w.globals.categoryLabels[dataPointIndex] + '</span> </div>'
	);
	const pointKey = w.globals.seriesNames[seriesIndex] + '|' + w.globals.categoryLabels[dataPointIndex];
	if (dataKeys.has(pointKey)) {
		text += (
			'<div> <span class="value start-value">' +
			distance + '</span> <span class="separator">±</span> <span class="value end-value">' +
			std + ' mm</span> </div></div>'
		);
	} else {
		text += '<div> <span class="value start-value">' + noDataText + '</span> </div></div>';
	}
	return text;
};


const getRangeChartOptions = (series, paasCoordsSelected, legendHeightBase, paasCoords, noDataText) => {
	let options = getBaseOptions('range_paas_coords', series);
	options.xaxis.type = 'category';
	options.xaxis.title.text = 'RINEX date';
	options.yaxis.title.text = 'millimeters';
	const MAX = 50000;
	const maxCalculated = Math.max(...paasCoordsSelected.flatMap(getRangeValue));
	if (maxCalculated > MAX) {
		options.yaxis.max = MAX;
	}
	options.fill.opacity = 0.3;
	options.tooltip.x.formatter = null;
	options.legend.height = (legendHeightBase >= 200) ? 200 : null;
	options.legend.showForSingleSeries = true;
	const dataKeys = new Set();
	for (const item of paasCoords) {
		dataKeys.add(item.mountpoint + '|' + item.rinex_date);
	}
	options.tooltip.custom = tooltipGenerator(dataKeys, noDataText);
	return options;
};


const useHeatmapSettings = (paasCoordsSelected, mountpoints, minDate, maxDate, getValueFunc) => {
	const seriesCategories = computed(() => generateSeries({
		items: paasCoordsSelected.value,
		datetimeField: 'rinex_date',
		getSeriesKeyFn: getSeriesKey,
		getValueFn: getValueFunc,
		seriesStep: 1,
		seriesStepUnit: 'day',
		categoryFormatter: dayJsToString,
		pointEntryFactory: pointEntryFactoryXYDay,
		minDate: minDate.value,
		maxDate: maxDate.value,
		forceSeriesKeys: mountpoints.value,
	}));
	const series = computed(() => seriesCategories.value.series);
	const categories = computed(() => seriesCategories.value.categories);
	return { series, categories };
}


const usePaasChart = (paasStore, mountpoints, minDate, maxDate, t) => {
	const paasCoordsSelected = computed(() => {
		if (mountpoints.value.length > 0) {
			return paasStore.paasCoords.filter(item => mountpoints.value.includes(item.mountpoint));
		}
		return paasStore.paasCoords;
	});

	const legendHeightBase = computed(() => Math.ceil(mountpoints.value.length / 13) * 16);
	const rangeChartHeight = computed(() => 400 + Math.min(legendHeightBase.value, 200));

	const rangeSeries = computed(() => generateRangeSeries(
		paasCoordsSelected.value,
		'rinex_date',
		getSeriesKey,
		getRangeValue,
		1, 'day',
		minDate.value, maxDate.value,
	));

	const rangeOptions = computed(() => getRangeChartOptions(
		rangeSeries.value,
		paasCoordsSelected.value,
		legendHeightBase.value,
		paasStore.paasCoords,
		t('paas.chart.noData'),
	));

	const heatmapMedianSettings = useHeatmapSettings(
		paasCoordsSelected,
		mountpoints,
		minDate,
		maxDate,
		getMedianValue,
	);

	const heatmapStddevSettings = useHeatmapSettings(
		paasCoordsSelected,
		mountpoints,
		minDate,
		maxDate,
		getStddevValue,
	);

	const hasData = computed(() => rangeSeries.value.length > 0);
	return {
		rangeChartHeight,
		rangeSeries,
		rangeOptions,
		heatmapMedianSeries: heatmapMedianSettings.series,
		heatmapMedianCategories: heatmapMedianSettings.categories,
		heatmapStddevSeries: heatmapStddevSettings.series,
		heatmapStddevCategories: heatmapStddevSettings.categories,
		hasData,
	}
};


export {
	calcCorrectionValues,
	usePaasChart,
}